// DSS Documentation on SocoAlertService - http://tiny.sc/cgsocoalertservice
// DSS Documentation on SocoAlertComponent - http://tiny.sc/cgsocoalertcomponent
import { Injectable } from '@angular/core';
import { Subject ,  Observable } from 'rxjs';

@Injectable()
/* istanbul ignore next */
export class SocoAlertService {
    private alertShowSource = new Subject<SocoAlertServiceOptions>();
    private alertHideSource = new Subject<SocoAlertServiceOptions>();
    alertShown$ = this.alertShowSource.asObservable();
    alertHidden$ = this.alertHideSource.asObservable();

    /* istanbul ignore next */
    /**
     *  Used to represent a messaging error in the application caused by the system/application
     *  @param options Primarily only id and message are needed. However, specify the options needed for the alert to update properly.
     */
    fail(options: SocoAlertServiceOptions): void {
        options.type = SocoAlertType.Failure;
        this.show(options);
    }

    /* istanbul ignore next */
    /**
    *  Used to represent a messaging error in the application caused by the user
    *  @param options Primarily only id and message are needed. However, specify the options needed for the alert to update properly.
    */
    error(options: SocoAlertServiceOptions): void {
        options.type = SocoAlertType.Error;
        this.show(options);
    }

    /* istanbul ignore next */
    /**
    *  Used to caution a user to yield and address a state that could prevent an error
    *  @param options Primarily only id and message are needed. However, specify the options needed for the alert to update properly.
    */
    caution(options: SocoAlertServiceOptions): void {
        options.type = SocoAlertType.Caution;
        this.show(options);
    }

    /* istanbul ignore next */
    /**
    * Used to represent data that will inform the user about a specific contextual situation within the system.
    *  @param options Primarily only id and message are needed. However, specify the options needed for the alert to update properly.
    */
    info(options: SocoAlertServiceOptions): void {
        options.type = SocoAlertType.Information;
        this.show(options);
    }

    /* istanbul ignore next */
    /**
    * Used to represent that the user successfully performed system input
    *  @param options Primarily only id and message are needed. However, specify the options needed for the alert to update properly.
    */
    success(options: SocoAlertServiceOptions): void {
        options.type = SocoAlertType.Success;
        this.show(options);
    }

    /* istanbul ignore next */
    /**
    * Hides all alerts
    */
    hide(): void {
        this.alertHideSource.next();
    }

    /* istanbul ignore next */
    /**
    * Returns the default alert id so it can be maintained in one place
    */
    getDefaultAlertId(): string {
        return 'soco-alert';
    }

    private show(options: SocoAlertServiceOptions): void {
        this.alertShowSource.next(options);
    }
}

/* istanbul ignore next */
export interface SocoAlertServiceOptions {
    /**
     *  The message that will appear in the alert
     */
    message: string;
    /**
     *  The location of the alert
     */
    location?: SocoAlertLocation;
    /**
     *  The type of alert (Danger, Warning, Info, or Success)
     */
    type?: SocoAlertType;
    /**
     *  If there are multiple alerts on the page, the id can be used to indicate which alert should display
     */
    id?: string;
}

export enum SocoAlertType {
    Unknown = 0,
    Error = 1,
    Failure = 2,
    Caution = 3,
    Information = 4,
    Success = 5
}

export enum SocoAlertLocation {
    System = 0,
    Page = 1,
    Section = 2
}
